import React, { useEffect, useMemo, useState } from "react"
import { compareDashboardKpi, getPreviousDashboardKpis, setDashboardPreviousPeriod } from "@/datox/api"
import { aggregateKpiValue, applyDashboardFilters, applyWidgetTableSettings } from "./DatoxComplianceDashboard.jsx"
import { widgetOutputTable } from "./dashboard-widget-data"

export const DEFAULT_KPI_COMPARISON = Object.freeze({
  version: 1,
  mode: "previous_period",
  previous_dashboard_id: null,
  previous_widget_id: null,
  visual: "bars",
  change: "both",
  direction: "higher_is_better",
})

export function isValidKpiComparison(config) {
  if (!config || typeof config !== "object" || Array.isArray(config)) return false
  const candidate = config
  return (
    candidate.version === 1 &&
    candidate.mode === "previous_period" &&
    (candidate.previous_dashboard_id === null || typeof candidate.previous_dashboard_id === "string") &&
    (candidate.previous_widget_id === null || typeof candidate.previous_widget_id === "string") &&
    (candidate.visual === "bars" || candidate.visual === "slope") &&
    (candidate.change === "both" || candidate.change === "amount" || candidate.change === "percentage") &&
    (candidate.direction === "higher_is_better" ||
      candidate.direction === "lower_is_better" ||
      candidate.direction === "neutral")
  )
}

export function useDashboardKpiComparisons(input) {
  const enabled = input.enabled !== false
  const comparisonWidgets = useMemo(
    () => input.widgets.filter((widget) => widget.type === "kpi" && isValidKpiComparison(widget.kpi_comparison)),
    [input.widgets],
  )
  const requestState = useMemo(
    () => buildRequestState(comparisonWidgets, input.outputTables, input.activeFilterWidgets ?? [], input.labels),
    [comparisonWidgets, input.outputTables, input.activeFilterWidgets, input.labels],
  )
  const requestSignature = JSON.stringify({
    revision: input.revision,
    widgets: comparisonWidgets.map((widget) => {
      const current = requestState.current_values[widget.id]
      const selection = widget.kpi_comparison || {}
      return [
        widget.id,
        selection.previous_dashboard_id || null,
        selection.previous_widget_id || null,
        Number.isFinite(current) ? current : null,
      ]
    }),
  })
  const [options, setOptions] = useState(null)
  const [optionsError, setOptionsError] = useState(null)
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [itemsByWidgetId, setItemsByWidgetId] = useState({})
  const [loadingComparisons, setLoadingComparisons] = useState(false)
  const [stale, setStale] = useState(false)
  const [staleMessage, setStaleMessage] = useState(null)
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    let active = true
    if (!enabled || !input.projectID || !input.dashboardID) {
      setOptions(null)
      setOptionsError(null)
      setOptionsLoading(false)
      return
    }
    setOptionsLoading(true)
    setOptionsError(null)
    // Bobur's analytics API (leshabank): previous-period KPIs, with the period list and the shared choice.
    void getPreviousDashboardKpis(input.projectID, input.dashboardID)
      .then((response) => {
        if (!active) return
        setOptions(response)
      })
      .catch((error) => {
        if (!active) return
        setOptions(null)
        setOptionsError(error instanceof Error ? error.message : input.labels.kpiComparisonSourcesUnavailable || "")
      })
      .finally(() => {
        if (active) setOptionsLoading(false)
      })
    return () => {
      active = false
    }
  }, [enabled, input.projectID, input.dashboardID, input.revision, refreshToken])

  useEffect(() => {
    let active = true
    if (!enabled || !input.projectID || !input.dashboardID || input.revision <= 0 || !requestState.widget_ids.length) {
      setItemsByWidgetId(requestState.local)
      setLoadingComparisons(false)
      setStale(false)
      setStaleMessage(null)
      return
    }
    setLoadingComparisons(true)
    setStale(false)
    setStaleMessage(null)
    const controller = new AbortController()
    // One request per KPI (Bobur's /analytics/kpi-comparison); a 409 means the dashboard changed under us.
    void Promise.all(
      comparisonWidgets
        .filter((widget) => requestState.widget_ids.includes(widget.id))
        .map((widget) =>
          compareDashboardKpi(
            input.projectID,
            input.dashboardID,
            widget,
            requestState.current_values[widget.id],
            input.revision,
            controller.signal,
          ).then(
            (item) => ({ ...item, widget_id: widget.id, state: item.status === "available" ? undefined : item.code }),
            (error) => {
              if (error?.status === 409) throw error
              return {
                widget_id: widget.id,
                status: "unavailable",
                code: "comparison_error",
                reason: input.labels.kpiComparisonUnavailable || "",
                current_value: requestState.current_values[widget.id],
                state: "comparison_error",
              }
            },
          ),
        ),
    )
      .then((items) => {
        if (!active) return
        const next = { ...requestState.local }
        for (const item of items) next[item.widget_id] = item
        setItemsByWidgetId(next)
      })
      .catch((error) => {
        if (!active) return
        if (error?.status === 409) {
          setStale(true)
          setStaleMessage(input.labels.kpiComparisonStaleReload || "")
          setItemsByWidgetId(
            Object.fromEntries(
              requestState.widget_ids.map((widgetId) => [
                widgetId,
                {
                  widget_id: widgetId,
                  status: "stale",
                  code: "stale_revision",
                  reason: input.labels.kpiComparisonStaleReload || "",
                  current_value: requestState.current_values[widgetId],
                  state: "stale",
                },
              ]),
            ),
          )
          return
        }
        setItemsByWidgetId(requestState.local)
      })
      .finally(() => {
        if (active) setLoadingComparisons(false)
      })
    return () => {
      active = false
      controller.abort()
    }
  }, [enabled, input.projectID, input.dashboardID, requestSignature, refreshToken])

  // The previous period is shared by the dashboard (Bobur's PUT /analytics/previous-period).
  const setPreviousPeriod = async (periodID) => {
    if (!input.projectID || !input.dashboardID) return
    setOptionsError(null)
    try {
      const next = await setDashboardPreviousPeriod(
        input.projectID,
        input.dashboardID,
        periodID || null,
        options?.previous_period?.id || null,
        input.revision,
      )
      setOptions(next)
    } catch {
      setOptionsError(input.labels.kpiComparisonSourcesUnavailable || input.labels.kpiComparisonUnavailable || "")
    }
  }

  return {
    options,
    setPreviousPeriod,
    loading: optionsLoading,
    error: optionsError,
    refetch: () => setRefreshToken((value) => value + 1),
    itemsByWidgetId,
    loadingComparisons,
    stale,
    staleMessage,
  }
}

export function KpiComparisonSettings(props) {
  const config = isValidKpiComparison(props.widget.kpi_comparison) ? props.widget.kpi_comparison : undefined
  const active = !!config
  const availableDashboards = props.options?.previous_period ? props.options.dashboards : []
  const selectedDashboardID = config?.previous_dashboard_id ?? availableDashboards[0]?.id ?? null
  const selectedDashboard =
    availableDashboards.find((dashboard) => dashboard.id === selectedDashboardID) ?? availableDashboards[0]
  const widgetChoices = selectedDashboard?.widgets ?? []
  const selectedWidgetID = config?.previous_widget_id ?? widgetChoices[0]?.id ?? null
  const selectedWidget = widgetChoices.find((widget) => widget.id === selectedWidgetID)
  const message =
    props.stateMessage ||
    comparisonSourceMessage(props.labels, props.options, selectedDashboard, selectedWidget, config)

  useEffect(() => {
    if (!active || !availableDashboards.length) return
    const nextDashboard =
      availableDashboards.find((dashboard) => dashboard.id === selectedDashboardID) || availableDashboards[0]
    const nextWidget =
      nextDashboard?.widgets?.find((widget) => widget.id === selectedWidgetID) || nextDashboard?.widgets?.[0]
    if (!nextDashboard?.id || !nextWidget?.id) return
    if (config?.previous_dashboard_id === nextDashboard.id && config?.previous_widget_id === nextWidget.id) return
    props.onChange({
      ...(config || DEFAULT_KPI_COMPARISON),
      previous_dashboard_id: nextDashboard.id,
      previous_widget_id: nextWidget.id,
    })
  }, [
    active,
    availableDashboards,
    config?.previous_dashboard_id,
    config?.previous_widget_id,
    props.onChange,
    selectedDashboardID,
    selectedWidgetID,
  ])

  return (
    <div className="datox-react-dashboard__comparison-settings">
      {props.options?.periods && props.onPreviousPeriod ? (
        <label>
          <div className="datox-react-dashboard__field-label">{props.labels.designer?.previousPeriod}</div>
          <select
            className="datox-react-dashboard__select"
            value={props.options.previous_period?.id || ""}
            disabled={!props.options.current_period}
            onChange={(event) => props.onPreviousPeriod(event.currentTarget.value)}
          >
            <option value="">{props.labels.designer?.none}</option>
            {props.options.periods.map((period) => (
              <option key={period.id} value={period.id}>
                {period.label}
              </option>
            ))}
          </select>
          <small>{props.labels.designer?.previousPeriodShared}</small>
        </label>
      ) : null}
      {props.options && props.options.previous_period ? (
        <label className="datox-react-dashboard__comparison-toggle">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) =>
              props.onChange(event.currentTarget.checked ? { ...DEFAULT_KPI_COMPARISON } : undefined)
            }
          />
          <span>{props.labels.kpiComparisonEnabled}</span>
        </label>
      ) : null}
      {active ? (
        <>
          <label>
            <div className="datox-react-dashboard__field-label">{props.labels.kpiComparisonPreviousDashboard}</div>
            <select
              className="datox-react-dashboard__select"
              value={selectedDashboardID || ""}
              onChange={(event) => {
                const nextDashboard = availableDashboards.find(
                  (dashboard) => dashboard.id === event.currentTarget.value,
                )
                const nextWidget = nextDashboard?.widgets?.[0]
                props.onChange({
                  ...(config || DEFAULT_KPI_COMPARISON),
                  previous_dashboard_id: nextDashboard?.id ?? null,
                  previous_widget_id: nextWidget?.id ?? null,
                })
              }}
              disabled={!availableDashboards.length}
            >
              {availableDashboards.map((dashboard) => (
                <option key={dashboard.id} value={dashboard.id}>
                  {dashboard.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <div className="datox-react-dashboard__field-label">{props.labels.kpiComparisonPreviousKpi}</div>
            <select
              className="datox-react-dashboard__select"
              value={selectedWidgetID || ""}
              onChange={(event) =>
                props.onChange({ ...(config || DEFAULT_KPI_COMPARISON), previous_widget_id: event.currentTarget.value })
              }
              disabled={!widgetChoices.length}
            >
              {widgetChoices.map((widget) => (
                <option key={widget.id} value={widget.id}>
                  {widget.title}
                </option>
              ))}
            </select>
          </label>
          <SegmentedSection
            label={props.labels.kpiComparisonChangeMode}
            value={config?.change || DEFAULT_KPI_COMPARISON.change}
            options={[
              { value: "both", label: props.labels.kpiComparisonBoth },
              { value: "amount", label: props.labels.kpiComparisonAmount },
              { value: "percentage", label: props.labels.kpiComparisonPercentage },
            ]}
            onChange={(change) => props.onChange({ ...(config || DEFAULT_KPI_COMPARISON), change })}
          />
          <SegmentedSection
            label={props.labels.kpiComparisonVisual}
            value={config?.visual || DEFAULT_KPI_COMPARISON.visual}
            options={[
              { value: "bars", label: props.labels.kpiComparisonBars },
              { value: "slope", label: props.labels.kpiComparisonSlope },
            ]}
            onChange={(visual) => props.onChange({ ...(config || DEFAULT_KPI_COMPARISON), visual })}
          />
          <SegmentedSection
            label={props.labels.kpiComparisonDirection}
            value={config?.direction || DEFAULT_KPI_COMPARISON.direction}
            options={[
              { value: "higher_is_better", label: props.labels.kpiComparisonHigherIsBetter },
              { value: "lower_is_better", label: props.labels.kpiComparisonLowerIsBetter },
              { value: "neutral", label: props.labels.kpiComparisonNeutral },
            ]}
            onChange={(direction) => props.onChange({ ...(config || DEFAULT_KPI_COMPARISON), direction })}
          />
          {message ? <div className="datox-react-dashboard__comparison-state">{message}</div> : null}
        </>
      ) : null}
      {props.isStale ? (
        <button
          type="button"
          className="datox-react-dashboard__button datox-react-dashboard__button--outline-primary datox-react-dashboard__comparison-reload"
          onClick={props.onReload}
        >
          {props.labels.kpiComparisonReload}
        </button>
      ) : null}
    </div>
  )
}

export function KpiComparisonDisplay(props) {
  const config = isValidKpiComparison(props.widget.kpi_comparison) ? props.widget.kpi_comparison : null
  if (!config || !props.result) return null
  if (props.result.status !== "available") {
    return (
      <div className="datox-react-dashboard__comparison-state">{comparisonStateText(props.labels, props.result)}</div>
    )
  }
  const favorable = trendTone(config.direction, props.result.amount_change ?? 0)
  const amountText = formatSignedNumber(
    props.result.amount_change ?? 0,
    props.widget.style,
    props.widget.style?.valueFormat === "percent" ? " pp" : "",
  )
  const percentageText =
    props.result.percentage_change == null
      ? props.labels.kpiComparisonPercentageUnavailable
      : `${formatSignedNumber(props.result.percentage_change, props.widget.style)}%`
  const barVisual =
    config.visual === "bars" ? (
      <BarsVisual current={props.result.current_value} previous={props.result.previous_value ?? 0} tone={favorable} />
    ) : (
      <SlopeVisual current={props.result.current_value} previous={props.result.previous_value ?? 0} tone={favorable} />
    )
  return (
    <div className={`datox-react-dashboard__comparison datox-react-dashboard__comparison--${favorable}`}>
      {barVisual}
      <div className="datox-react-dashboard__comparison-values">
        {config.change !== "percentage" ? <strong>{amountText}</strong> : null}
        {config.change === "both" ? (
          <span>{percentageText}</span>
        ) : config.change === "percentage" ? (
          <strong>{percentageText}</strong>
        ) : null}
      </div>
    </div>
  )
}

function buildRequestState(widgets, outputTables, filterWidgets, labels) {
  const current_values = {}
  const local = {}
  const widget_ids = []
  widgets.forEach((widget) => {
    const table = widgetTable(widget, outputTables, filterWidgets)
    const current = aggregateKpiValue(table, widget.data || {})
    if (!Number.isFinite(current)) {
      local[widget.id] = {
        widget_id: widget.id,
        status: "unavailable",
        code: "current_widget_unavailable",
        reason: labels.kpiComparisonCurrentUnavailable || "",
        current_value: 0,
        state: "current_widget_unavailable",
      }
      return
    }
    widget_ids.push(widget.id)
    current_values[widget.id] = current
  })
  return { widget_ids, current_values, local }
}

function widgetTable(widget, outputTables, filterWidgets) {
  const table = widgetOutputTable(outputTables, widget)
  return applyWidgetTableSettings(applyDashboardFilters(table, widget, filterWidgets), widget.data || {})
}

function comparisonSourceMessage(labels, options, dashboard, widget, config) {
  if (!options || options.previous_period == null) return labels.kpiComparisonNoPreviousPeriod
  if (!options.dashboards.length) return labels.kpiComparisonPreviousNotPublished
  if (config?.previous_dashboard_id && !dashboard) return labels.kpiComparisonPreviousDashboardMissing
  if (config?.previous_widget_id && !widget) return labels.kpiComparisonNoPublishedValue
  return ""
}

function comparisonStateText(labels, result) {
  if (result.status === "stale") return labels.kpiComparisonStale
  if (result.code === "selection_required") return labels.kpiComparisonNeedsSelection
  if (result.code === "unit_mismatch") return labels.kpiComparisonUnitMismatch
  if (result.code === "previous_value_missing") return labels.kpiComparisonNoPublishedValue
  if (result.code === "current_widget_unavailable") return labels.kpiComparisonCurrentUnavailable
  return result.reason || labels.kpiComparisonUnavailable
}

function trendTone(direction, amount) {
  if (!amount) return "neutral"
  if (direction === "neutral") return "neutral"
  const positive = amount > 0
  return (direction === "higher_is_better" && positive) || (direction === "lower_is_better" && !positive)
    ? "positive"
    : "negative"
}

function formatSignedNumber(value, style, suffix = "") {
  const decimals = Number.isFinite(Number(style?.decimalPlaces)) ? Number(style?.decimalPlaces) : 2
  const text = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: "exceptZero",
  }).format(Number(value))
  return `${text}${suffix}`
}

function BarsVisual({ current, previous, tone }) {
  const max = Math.max(Math.abs(current), Math.abs(previous), 1)
  return (
    <div
      className={`datox-react-dashboard__comparison-visual datox-react-dashboard__comparison-visual--bars datox-react-dashboard__comparison-visual--${tone}`}
    >
      <span style={{ height: `${Math.max(10, Math.round((Math.abs(previous) / max) * 100))}%` }} />
      <span style={{ height: `${Math.max(10, Math.round((Math.abs(current) / max) * 100))}%` }} />
    </div>
  )
}

function SlopeVisual({ current, previous, tone }) {
  const min = Math.min(previous, current)
  const max = Math.max(previous, current)
  const span = max - min || 1
  const y1 = 24 - ((previous - min) / span) * 16
  const y2 = 24 - ((current - min) / span) * 16
  return (
    <svg
      className={`datox-react-dashboard__comparison-visual datox-react-dashboard__comparison-visual--slope datox-react-dashboard__comparison-visual--${tone}`}
      viewBox="0 0 56 28"
      aria-hidden="true"
    >
      <line x1="10" y1={y1} x2="46" y2={y2} />
      <circle cx="10" cy={y1} r="2.5" />
      <circle cx="46" cy={y2} r="2.5" />
    </svg>
  )
}

function SegmentedSection(props) {
  return (
    <div className="datox-react-dashboard__comparison-segmented">
      <div className="datox-react-dashboard__field-label">{props.label}</div>
      <div className="datox-react-dashboard__comparison-segmented-row">
        {props.options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={
              option.value === props.value
                ? "datox-react-dashboard__comparison-segmented-item datox-react-dashboard__comparison-segmented-item--active"
                : "datox-react-dashboard__comparison-segmented-item"
            }
            onClick={() => props.onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
